import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form, FormControl, FormField, FormItem, FormMessage, RadioGroup } from "@patient-watch/ui";
import { formSearchParamsSchema } from "../../types/formSearchParams";
import FormStepWrapper from "../../components/FormStepWrapper";
import FormButtons from "../../components/FormButtons";
import { useFormData } from "../../hooks/useFormData";
import { useFormSubmission } from "../../hooks/useFormSubmission";
import StyledRadioItem from "../../components/StyledRadioItem";
import { useCopy } from "../../i18n/locale";

const formSchema = (message: string) =>
    z.object({
        at_sea_all_day: z.enum(["yes", "no"], {
            required_error: message,
        }),
    });

type FormData = z.infer<ReturnType<typeof formSchema>>;

export const Route = createFileRoute("/form/at-sea-all-day")({
    component: AtSeaAllDayComponent,
    validateSearch: (search) => formSearchParamsSchema.parse(search),
});

function AtSeaAllDayComponent() {
    const { entry_id, profile_id } = useSearch({
        from: "/form/at-sea-all-day",
    });
    const navigate = useNavigate();
    const copy = useCopy();

    const form = useForm<FormData>({
        resolver: zodResolver(formSchema(copy.common.selectOption)),
    });

    useFormData({
        entry_id,
        form,
        fields: ["at_sea_all_day"],
        transform: {
            at_sea_all_day: (value: boolean | null | undefined) => {
                if (value === true) return "yes";
                if (value === false) return "no";
                return undefined;
            },
        },
    });

    const { submitting, submitForm } = useFormSubmission({
        entry_id,
        profile_id,
        onSuccess: () => {
            if (form.getValues("at_sea_all_day") === "yes") {
                navigate({
                    to: "/form/on-duty-intervals",
                    search: { entry_id, profile_id } as any,
                });
            } else {
                navigate({
                    to: "/form/at-sea-intervals",
                    search: { entry_id, profile_id } as any,
                });
            }
        },
    });

    const onSubmit = async (data: FormData) => {
        const booleanValue = data.at_sea_all_day === "yes";
        await submitForm(
            { at_sea_all_day: booleanValue },
            !booleanValue ? "/form/at-sea-intervals" : "/form/on-duty-intervals"
        );
    };

    const onPrevious = () => {
        navigate({
            to: "/form/at-sea",
            search: { entry_id, profile_id } as any,
        });
    };

    const options = [
        {
            value: "yes",
            label: copy.common.yes,
            description: copy.atSeaAllDay.yes,
            color: "text-green-600",
            bgColor: "bg-green-50",
            borderColor: "border-green-200",
            selectedBgColor: "bg-green-100",
            selectedBorderColor: "border-green-400",
        },
        {
            value: "no",
            label: copy.common.no,
            description: copy.atSeaAllDay.no,
            color: "text-blue-600",
            bgColor: "bg-blue-50",
            borderColor: "border-blue-200",
            selectedBgColor: "bg-blue-100",
            selectedBorderColor: "border-blue-400",
        },
    ];

    return (
        <FormStepWrapper currentStep={1}>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 py-4 w-full max-w-md">
                    <h2 className="block font-semibold text-xl text-center">{copy.atSeaAllDay.question}</h2>
                    <FormField
                        control={form.control}
                        name="at_sea_all_day"
                        render={({ field }) => (
                            <FormItem className="space-y-3">
                                <FormControl>
                                    <RadioGroup
                                        onValueChange={field.onChange}
                                        value={field.value}
                                        className="space-y-3"
                                    >
                                        {options.map((option) => {
                                            const isSelected = field.value === option.value;

                                            return (
                                                <StyledRadioItem
                                                    key={option.value}
                                                    option={option}
                                                    isSelected={isSelected}
                                                />
                                            );
                                        })}
                                    </RadioGroup>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormButtons submitting={submitting} showPrevious onPrevious={onPrevious} />
                </form>
            </Form>
        </FormStepWrapper>
    );
}
